import React, { useEffect, useState, useContext } from "react";
import { Button, Divider, Form, Input, notification } from "antd";
import { useHistory, useLocation } from "react-router";

import PasswordValidator from "@Components/user/PasswordValidator";
import { hasLowerCase, hasNumber, hasSpecialChar, hasUpperCase, Rules } from "@Components/user/Security";
import { ISetPasswordDetails } from "@Models/IRegisterDetails";
import { IValue } from "@Models/IValue";
import SessionManager from "@Core/session";
import AccountService from "@Services/AccountService";
import { MainContext } from "@Context/provider";
import { ViewMode } from "@Context/state";

import "@Styles/components/updateForm.scss";

const defaultRules: Rules = {
    minChar: { description: "Be at least 8 characters", checked: false },
    lower: { description: "Contain a lowercase letter", checked: false },
    upper: { description: "Contain an uppercase letter", checked: false },
    number: { description: "Contain a number", checked: false },
    special: { description: "Contain a special character", checked: false },
};

interface PasswordUpdateFormProps {
    type: "reset" | "update";
}

const PasswordUpdateForm: React.FC<PasswordUpdateFormProps> = (props: PasswordUpdateFormProps) => {
    const location = useLocation();
    const history = useHistory();
    const [form] = Form.useForm();
    const { ui: uiState } = useContext(MainContext);
    const isDesktop = uiState.mode === ViewMode.desktop;

    const [passwordRules, setPasswordRules] = useState(defaultRules);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [unmetRules, setUnmetRules] = useState<any>([]);
    const [confirmMatch, setConfirmMatch] = useState<boolean>(false);

    const extractPayload = (search: string) => {
        return search.split("?account=")[1];
    };

    useEffect(() => {
        let timeout;
        if (formSubmitted) {
            timeout = setTimeout(() => {
                history.push("/login");
            }, 5000);
        }
        return () => clearTimeout(timeout);
    }, [formSubmitted]);

    const unmetValidationRules = (rules: Rules) => {
        return Object.values(rules).filter((element) => {
            return element.checked === false;
        });
    };

    const handleChangePassword = (event, passwordRules) => {
        const { value } = event.target;
        form.setFieldsValue({ password: value });
        form.validateFields(["password"]).catch(() => {
            // catch the error only | no need to handle it here
        });

        let minChar;
        let lower;
        let upper;
        let number;
        let special;

        // check length
        if (value.length >= 8) {
            minChar = { ...passwordRules.minChar, checked: true };
        } else {
            minChar = { ...passwordRules.minChar, checked: false };
        }

        // check lowercase
        if (hasLowerCase(value)) {
            lower = { ...passwordRules.lower, checked: true };
        } else {
            lower = { ...passwordRules.lower, checked: false };
        }

        // check uppercase
        if (hasUpperCase(value)) {
            upper = { ...passwordRules.upper, checked: true };
        } else {
            upper = { ...passwordRules.upper, checked: false };
        }

        // check number
        if (hasNumber(value)) {
            number = { ...passwordRules.number, checked: true };
        } else {
            number = { ...passwordRules.number, checked: false };
        }

        // check special character
        if (hasSpecialChar(value)) {
            special = { ...passwordRules.special, checked: true };
        } else {
            special = { ...passwordRules.special, checked: false };
        }

        setPasswordRules({ minChar, lower, upper, number, special });
    };

    const submitReset = async (values) => {
        const resetPayload = extractPayload(location.search);
        const res: ISetPasswordDetails = { password: values.password, resetPayload };
        const service = new AccountService();
        const result = await service.setPassword(res);
        if (result.hasErrors) {
            notification.error({ message: "Reset password error" });
        }
    };

    const submitUpdate = async (values) => {
        const res: IValue = { value: values.password };
        const service = new AccountService();
        const result = await service.updatePassword(SessionManager.getServiceUser()?.userId, res);

        if (result.hasErrors) {
            notification.error({ message: "Update password error" });
        }
    };
    /**
     * Form completion.
     * @param values
     */
    const onSubmit = async (values) => {
        setFormSubmitted(true);
        if (props.type === "reset") {
            await submitReset(values);
        }
        if (props.type === "update") {
            await submitUpdate(values);
        }
    };

    return (
        <section>
            <Form form={form} className="update-pass-form" onFinish={onSubmit}>
                <PasswordValidator passwordRules={passwordRules} />
                <Divider />
                <Form.Item
                    name="password"
                    rules={[
                        {
                            required: true,
                            message: "Please input your new password",
                        },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                setUnmetRules(unmetValidationRules(passwordRules));
                                if (unmetRules.length > 0) {
                                    return Promise.reject(
                                        new Error(
                                            "Please ensure that your password adheres to all of the criteria above."
                                        )
                                    );
                                }
                                return Promise.resolve();
                            },
                        }),
                    ]}
                >
                    <Input.Password
                        placeholder="Input new password"
                        onChange={(e) => handleChangePassword(e, passwordRules)}
                    />
                </Form.Item>
                <Form.Item
                    name="confirmPassword"
                    dependencies={["password"]}
                    rules={[
                        {
                            required: true,
                            message: "Please confirm your password",
                        },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue("password") !== value) {
                                    setConfirmMatch(false);
                                    return Promise.reject(new Error("Please ensure that both passwords match."));
                                }
                                setConfirmMatch(true);
                                return Promise.resolve();
                            },
                        }),
                    ]}
                >
                    <Input.Password placeholder="Confirm password" />
                </Form.Item>

                {formSubmitted && (
                    <p className="centered update-pass-form-text" style={isDesktop ? { color: "black" } : null}>
                        Your password has been changed successfully. Redirecting you to the login page...
                    </p>
                )}

                <Button
                    type="primary"
                    disabled={unmetRules.length > 0 || !confirmMatch}
                    htmlType="submit"
                    loading={formSubmitted}
                >
                    Confirm
                </Button>
            </Form>
        </section>
    );
};

export default PasswordUpdateForm;
